/**
 * DesktopItemGallery - Galleria ottimizzata per Desktop
 * 
 * Caratteristiche:
 * - Layout 2 colonne: main image + thumbnails laterali
 * - Full height con sticky position
 * - Zoom con mouse wheel
 * - Keyboard navigation (frecce, ESC)
 * - Lightbox fullscreen
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { 
  ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut, Maximize2, 
  Play, ArrowLeft
} from 'lucide-react';
import { fixImageUrl } from '../utils/images';

interface DesktopItemGalleryProps {
  images: string[];
  videos?: string[];
  title: string;
  distance?: number | null;
  status?: string;
  onBack?: () => void;
}

type MediaItem = {
  type: 'image' | 'video';
  url: string;
  thumbnail?: string;
  videoType?: 'mp4' | 'youtube';
  youtubeId?: string;
};

// Funzione per determinare il tipo di video
function getVideoType(url: string): { type: 'mp4' | 'youtube'; id?: string } {
  if (!url) return { type: 'mp4' };
  
  // YouTube patterns
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/,
    /youtube\.com\/watch\?.*v=([^&\s?]+)/,
  ];
  
  for (const pattern of youtubePatterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return { type: 'youtube', id: match[1] };
    }
  }
  
  return { type: 'mp4' };
}

export default function DesktopItemGallery({ 
  images, 
  videos = [], 
  title, 
  distance, 
  status,
  onBack
}: DesktopItemGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxZoomed, setLightboxZoomed] = useState(false);
  const [imageError, setImageError] = useState<Set<number>>(new Set());

  // Combina immagini e video
  const mediaItems: MediaItem[] = useMemo(() => {
    const items: MediaItem[] = [];
    
    if (images && images.length > 0) {
      images.forEach((img: string) => {
        items.push({ type: 'image', url: fixImageUrl(img) });
      });
    }
    
    if (videos && videos.length > 0) {
      videos.forEach((vid: string) => {
        const videoInfo = getVideoType(vid);
        items.push({ 
          type: 'video', 
          url: vid,
          videoType: videoInfo.type,
          youtubeId: videoInfo.id
        });
      });
    }
    
    return items;
  }, [images, videos]);

  const currentMedia = mediaItems[currentIndex];
  const totalItems = mediaItems.length;

  // Navigation
  const goToPrev = useCallback(() => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : totalItems - 1));
    setIsZoomed(false);
  }, [totalItems]);

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev < totalItems - 1 ? prev + 1 : 0));
    setIsZoomed(false);
  }, [totalItems]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showLightbox) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          goToPrev();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        case 'Escape':
          setShowLightbox(false);
          break;
        case '+':
        case '=':
          setIsZoomed(true);
          break;
        case '-':
          setIsZoomed(false);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showLightbox, goToPrev, goToNext]);

  // Mouse wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      setIsZoomed(prev => !prev);
    }
  }, []);

  // Thumbnail click
  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
    setIsZoomed(false);
  };

  // Toggle zoom
  const toggleZoom = () => {
    if (currentMedia?.type === 'image') {
      setIsZoomed(!isZoomed);
    }
  };

  // Open lightbox
  const openLightbox = () => {
    if (currentMedia?.type === 'image') {
      setShowLightbox(true);
    }
  };

  // Handle image error
  const handleImageError = (index: number) => {
    setImageError(prev => new Set(prev).add(index));
  };

  // Status badge config
  const getStatusConfig = (status?: string) => {
    switch (status) {
      case 'reserved':
        return { bg: 'bg-yellow-500', text: 'Prenotato' };
      case 'sold':
        return { bg: 'bg-red-500', text: 'Venduto' };
      case 'available':
      default:
        return { bg: 'bg-green-500', text: 'Disponibile' };
    }
  };

  const statusConfig = getStatusConfig(status);

  // Nessun media
  if (!mediaItems || mediaItems.length === 0) {
    return (
      <div className="h-full bg-gray-100 rounded-xl flex items-center justify-center min-h-[400px]">
        <div className="text-center text-gray-400">
          <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
            <span className="text-4xl">🚤</span>
          </div>
          <p className="text-lg">Nessuna foto disponibile</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header con back button e title */}
      {onBack && (
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-sky-500 hover:text-sky-600 transition-all shadow-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm font-medium">Torna</span>
          </button>
          <h2 className="text-lg font-semibold text-gray-900 truncate">{title}</h2>
        </div>
      )}

      {/* Main Gallery Container - Full Height */}
      <div 
        className="flex-1 flex gap-4 min-h-0"
        onWheel={handleWheel}
      >
        {/* Main Image/Video - 75% width */}
        <div className={`flex-[3] relative bg-gray-900 rounded-xl overflow-hidden group`}>
          {currentMedia?.type === 'image' ? (
            <div 
              className="w-full h-full cursor-zoom-in relative"
              onClick={openLightbox}
              title="Clicca per fullscreen"
            >
              {!imageError.has(currentIndex) ? (
                <img 
                  src={currentMedia.url} 
                  alt={`${title} - ${currentIndex + 1}`}
                  className="w-full h-full object-contain"
                  onError={() => handleImageError(currentIndex)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-800">
                  <div className="text-center text-gray-500">
                    <span className="text-4xl">🖼️</span>
                    <p>Immagine non disponibile</p>
                  </div>
                </div>
              )}
              
              {/* Fullscreen indicator */}
              <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                <ZoomIn className="w-4 h-4" />
                <span>Clicca per fullscreen</span>
              </div>
            </div>
          ) : currentMedia?.videoType === 'youtube' && currentMedia?.youtubeId ? (
            <div className="w-full h-full flex items-center justify-center bg-black">
              <iframe
                src={`https://www.youtube.com/embed/${currentMedia.youtubeId}?autoplay=0&rel=0`}
                title="YouTube video"
                className="w-full h-full max-w-full max-h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-black">
              <video 
                src={currentMedia?.url}
                controls
                className="w-full h-full object-contain"
                playsInline
              >
                Il tuo browser non supporta il video.
              </video>
            </div>
          )}
          
          {/* Video indicator */}
          {currentMedia?.type === 'video' && (
            <div className="absolute top-4 left-4 bg-red-600 text-white font-bold px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg">
              <Play className="w-5 h-5" />
              {currentMedia.videoType === 'youtube' ? 'YouTube' : 'Video'}
            </div>
          )}

          {/* Status Badge */}
          {status && (
            <div className={`absolute top-4 right-4 ${statusConfig.bg} text-white font-bold px-4 py-2 rounded-lg shadow-lg`}>
              {statusConfig.text}
            </div>
          )}

          {/* Distance Badge */}
          {distance !== null && distance !== undefined && (
            <div className="absolute bottom-4 right-4 bg-sky-600 text-white font-bold px-4 py-2 rounded-lg shadow-lg">
              {distance} km da te
            </div>
          )}

          {/* Navigation arrows */}
          {totalItems > 1 && (
            <>
              <button
                onClick={goToPrev}
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all opacity-0 group-hover:opacity-100"
                title="Precedente (←)"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-3 rounded-full transition-all opacity-0 group-hover:opacity-100"
                title="Successivo (→)"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Counter badge */}
          <div className="absolute bottom-4 left-4 bg-black/70 text-white font-bold px-4 py-2 rounded-lg">
            {currentIndex + 1} / {totalItems}
          </div>

          {/* Fullscreen button */}
          {currentMedia?.type === 'image' && (
            <button
              onClick={openLightbox}
              className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-lg transition-all opacity-0 group-hover:opacity-100"
              title="Schermo intero"
            >
              <Maximize2 className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Thumbnails - 25% width */}
        <div className="flex-1 flex flex-col min-w-[140px] max-w-[180px]">
          <div className="flex-1 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
            {mediaItems.map((media, idx) => (
              <button
                key={idx}
                onClick={() => handleThumbnailClick(idx)}
                className={`w-full aspect-video rounded-lg overflow-hidden border-2 transition-all relative ${
                  idx === currentIndex 
                    ? 'border-sky-500 ring-2 ring-sky-200' 
                    : 'border-gray-200 hover:border-gray-400'
                }`}
              >
                {media.type === 'image' ? (
                  !imageError.has(idx) ? (
                    <img 
                      src={media.url} 
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={() => handleImageError(idx)}
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <span className="text-xl">🖼️</span>
                    </div>
                  )
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center relative">
                    <div className="absolute inset-0 bg-black/20" />
                    <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow-lg z-10">
                      <Play className="w-5 h-5 text-red-600 ml-0.5" fill="currentColor" />
                    </div>
                  </div>
                )}
                
                {/* Video Badge - mostra su tutti i video */}
                {media.type === 'video' && (
                  <div className="absolute top-1 left-1 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded flex items-center gap-1 shadow-md">
                    <Play className="w-2.5 h-2.5" fill="currentColor" />
                    VIDEO
                  </div>
                )}
                
                {/* Active indicator */}
                {idx === currentIndex && (
                  <div className="absolute inset-0 bg-sky-500/20" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox Fullscreen */}
      {showLightbox && currentMedia?.type === 'image' && (
        <div
          className="fixed inset-0 z-[100] bg-gray-800 flex items-center justify-center"
          onClick={() => setShowLightbox(false)}
        >
          {/* Close button */}
          <button
            className="absolute top-6 right-6 text-white/80 hover:text-white p-3 hover:bg-white/10 rounded-full transition-all z-10"
            onClick={() => setShowLightbox(false)}
          >
            <X className="w-8 h-8" />
          </button>

          {/* Navigation buttons */}
          {totalItems > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goToPrev(); }}
                className="absolute left-6 text-white/80 hover:text-white p-4 bg-white/10 hover:bg-white/20 rounded-full transition-all"
              >
                <ChevronLeft className="w-10 h-10" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); goToNext(); }}
                className="absolute right-6 text-white/80 hover:text-white p-4 bg-white/10 hover:bg-white/20 rounded-full transition-all"
              >
                <ChevronRight className="w-10 h-10" />
              </button>
            </>
          )}

          {/* Counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 text-white px-6 py-3 rounded-full font-medium">
            {currentIndex + 1} / {totalItems}
          </div>

          {/* Zoom button */}
          {currentMedia?.type === 'image' && (
            <button
              onClick={(e) => { e.stopPropagation(); setLightboxZoomed(!lightboxZoomed); }}
              className="absolute top-6 left-6 text-white/80 hover:text-white p-3 hover:bg-white/10 rounded-full transition-all z-10"
              title={lightboxZoomed ? 'Zoom out' : 'Zoom in'}
            >
              {lightboxZoomed ? <ZoomOut className="w-6 h-6" /> : <ZoomIn className="w-6 h-6" />}
            </button>
          )}

          {/* Image */}
          <img 
            src={currentMedia.url} 
            alt={`${title} - ${currentIndex + 1}`}
            className={`max-w-[90vw] max-h-[85vh] object-contain transition-transform duration-300 ${
              lightboxZoomed ? 'scale-200 cursor-zoom-out' : 'cursor-zoom-in'
            }`}
            onClick={(e) => { 
              e.stopPropagation(); 
              if (lightboxZoomed) setLightboxZoomed(false);
            }}
          />

          {/* Keyboard hints */}
          <div className="absolute bottom-6 right-6 flex gap-3">
            <span className="text-white/50 text-sm">← → Naviga</span>
            <span className="text-white/50 text-sm">{lightboxZoomed ? ' - Zoom' : ''} ESC Chiudi</span>
          </div>
        </div>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a1a1a1;
        }
      `}</style>
    </div>
  );
}
